import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Pressable, View } from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import {
  Absolute,
  BodyText,
  Button,
  HeaderText,
  PortalOverlay,
  PortalOverlayHeader,
  PortalOverlayHeaderIconBtn,
  PortalOverlayModal,
  Stack,
} from '../../../ui';
import {
  useAssignTest,
  useInTest,
  useIsMobile,
} from '../../../../hooks';
import { InstagramLinkContainer } from './InstagramLink/InstagramLinkContainer';
import RatingStars from '../../../RatingStars';
import InsightsCTAs from '../../../provider/InsightsCTAs';
import BookmarkIcon from './BookmarkIcon/BookmarkIcon';
import DirectShareButton from '../../../consumer/sharing/DirectShareButton';
import { BasicPencilCreateIcon } from '../../../ui/Icons/BasicPencilCreateIcon';
import { PublicProvider } from '../../../../api/Providers';
import type { RootState } from '../../../../store/models';
import type { State as CurrentUserState, WhoAmI } from '../../../../store/CurrentUser.types';
import { CLIENT_PROPROFILE_CLARIFY_LOCATION_TEST_NAME, CLIENT_PROPROFILE_CLARIFY_LOCATION_TEST_PERCENT } from '../constants';
import {
  ArrowsChevronLeftIcon,
  BasicHomeIcon,
  ShoppingBusinessIcon,
} from '../../../ui/Icons';
import ProBusinessInfoCard from '../AboutTab/ProBusinessInfoCard';
import { ProviderScheduleDay } from '../../../../store/ProviderWorkSchedule.types';
import { googleMapURL } from '../../../../modules/maps/location';
import analytics from '../../../../modules/analytics';
import AvatarIcon from '../../../AvatarIcon';
import { ShareTarget } from '../../../../modules/share';
import { ShareProps } from '../../../consumer/sharing';
import { DeprecatedAboutBusinessHours } from '../BusinessHours';
import CancellationPolicy from '../CancellationPolicy';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';
import { getBusinessName, getProfileLocation } from '../../../../api/ProfileClientView/ProfileClientView';
import { openExternalUrl } from '../../../../modules/openExternalUrl';
import { DeprecatedTopProBadge } from '../../TopPro/DeprecatedTopProBadge';
import { daysSince } from '../../../provider/TopPro/dateUtils';
import { NEW_PRO_MIN_REVIEWS, NEW_PRO_DAYS } from '../../NewPro/NewProConstants';
import { NewProBadge } from '../../NewPro/NewProBadge';
import { AboutProDepositPolicy } from '../DepositPolicy';
import { formatNameWithPronouns } from '../../../../modules/formatters/formatNameWithPronouns';

type BusinessInfoModalProps = {
  isMobile: boolean;
  provider: PublicProvider | ClientProviderProfile;
  proSchedule: Array<ProviderScheduleDay>;
  showCallPro: boolean;
  onMapPress: () => void;
  onBackPress: () => void;
  onMessagePro: () => void;
  onCallPro: () => void;
};

const BusinessInfoModal: React.FCWithChildren<BusinessInfoModalProps> = ({
  isMobile,
  provider,
  proSchedule,
  showCallPro,
  onMapPress,
  onBackPress,
  onMessagePro,
  onCallPro,
}) => (
  <PortalOverlay animationStyle={isMobile ? 'slideLeft' : 'fadeIn'} name="pro-location-modal">
    <PortalOverlayModal position="fullScreen">
      <PortalOverlayHeader
        renderTitle={() => (
          <Box alignItems="center" justifyContent="center">
            <HeaderText level={2}>
              {provider.name}&apos;s Location
            </HeaderText>
          </Box>
        )}
        renderLeftAction={() => (
          <PortalOverlayHeaderIconBtn
            position="left"
            iconTestID="pro-location-back-icon"
            testID="pro-location-back"
            onPress={onBackPress}
            Icon={ArrowsChevronLeftIcon}
          />
        )}
      />
      <ProBusinessInfoCard
        provider={provider}
        businessHours={<DeprecatedAboutBusinessHours schedule={proSchedule} />}
        cancellationPolicy={provider.cancellation_policy_text ? (
          <CancellationPolicy
            providerName={provider.name}
            cancellationPolicyText={provider.cancellation_policy_text}
          />
        ) : null}
        depositPolicy={provider.deposit_policy_text ? (
          <AboutProDepositPolicy
            providerName={provider.name}
            depositPolicyText={provider.deposit_policy_text}
          />
        ) : null}
        onPressMap={onMapPress}
        contactActions={(
          <Inline
            space={16}
            justifyContent="center"
          >
            <Button
              onClick={onMessagePro}
              appearance="outline"
              size="medium"
              testID="messageButton"
            >
              Message
            </Button>
            {showCallPro && (
              <Button
                onClick={onCallPro}
                appearance="outline"
                size="medium"
                testID="callButton"
              >
                Call
              </Button>
            )}
          </Inline>
        )}
      />
    </PortalOverlayModal>
  </PortalOverlay>
);

type ProfileActionsProps = {
  provider: PublicProvider | ClientProviderProfile;
  isEditMode: boolean;
  isBookmarked: boolean;
  user?: CurrentUserState;
  shareConfig?: Omit<ShareProps, 'user'>;
  onBookmarkClick: () => void;
  onProfileEditClick: () => void;
  shouldShowChat?: boolean;
  onChatClick?: () => void;
};

const ProfileActions: React.FCWithChildren<ProfileActionsProps> = ({
  isBookmarked,
  isEditMode,
  provider,
  user,
  shareConfig,
  onBookmarkClick,
  onProfileEditClick,
  shouldShowChat = false,
  onChatClick,
}) => (!isEditMode && !!shareConfig
  // Client actions, only available in preview mode
  ? (
    <Inline space={12} paddingTop={16} paddingBottom={24}>
      {shouldShowChat && (
        <Box flexGrow={0} flexBasis="auto" justifyContent="center">
          <Button
            onClick={onChatClick}
            appearance="primary-neue"
            size="small"
            testID="pro-bio-chat-entry-point"
            clickTracking="pro_bio_chat_clicked"
          >
            Message
          </Button>
        </Box>
      )}
      <Box flexGrow={0} flexBasis="auto">
        {/* Hearting the pro */}
        <BookmarkIcon
          onClick={onBookmarkClick}
          bookmarked={isBookmarked}
        />
      </Box>
      <Box flexGrow={0} flexBasis="auto">
        {/* Share profile */}
        <DirectShareButton
          {...shareConfig}
          appearance="basic-share-arrow-icon"
          target={ShareTarget.PROFILE}
          provider={provider}
          user={user as WhoAmI}
        />
      </Box>
    </Inline>
  )
  // Pro actions, only available in edit mode
  : (
    <Box flexGrow={0} flexBasis="auto">
      <Pressable onPress={onProfileEditClick} testID="proEditProfileIcon">
        <Box>
          <BasicPencilCreateIcon
            color={BrandColor.Blue}
          />
        </Box>
      </Pressable>
    </Box>
  ));

/**
 * Gets a summary address, which is either salon name, address line 1/city/state/zip, or
 * city/state/zip
 * @param profile The profile from which to get address information
 * @returns The consolidated address
 */
const consolidateAddress = (profile: PublicProvider | ClientProviderProfile): string => {
  const location = getProfileLocation(profile);
  const businessName = getBusinessName(profile);

  if (businessName) {
    return businessName;
  }

  if (!location) {
    return '';
  }

  return [
    location.mobile_business ? '' : location.address1,
    location.city,
    location.state,
    location.zipcode,
  ].filter(Boolean).join(', ');
};

export type ProBioProps = {
  provider: PublicProvider | ClientProviderProfile;
  isEditMode: boolean;
  isTopPro: boolean;
  ratingCount?: number;
  reviewCount?: number;
  isBookmarked: boolean;
  isOwnProfile: boolean;
  showCallPro: boolean;
  onProfilePhotoClick: () => void;
  onBookmarkClick: () => void;
  onProfileEditClick: () => void;
  onMessagePro: () => void;
  onCallPro: () => void;
  onTopProClick?: () => void;
  shareConfig: Omit<ShareProps, 'user'>;
  shouldShowChat?: boolean;
  onMessageClicked?: () => Promise<any>;
};

export const DeprecatedProBio: React.FCWithChildren<ProBioProps> = ({
  provider,
  isEditMode,
  isTopPro,
  ratingCount,
  reviewCount,
  isBookmarked,
  isOwnProfile,
  showCallPro,
  onProfilePhotoClick,
  onBookmarkClick,
  onProfileEditClick,
  onCallPro,
  onMessagePro,
  onTopProClick,
  shareConfig,
  shouldShowChat = false,
  onMessageClicked,
}) => {
  const user = useSelector<RootState, CurrentUserState>(
    state => state.user,
    (prevUser, newUser) => prevUser.userId === newUser.userId,
  );
  const isProvider = !!user.providerId;
  const location = getProfileLocation(provider);
  const {
    id: providerId,
    profile_photo: profilePhoto,
    name,
    profession,
    rating_stars: ratingStars,
    creation_time: proCreationTime,
    preferred_pronouns: preferredPronouns,
  } = provider;
  const isMobile = useIsMobile();
  // In edit mode if the business name isn't set, show the pro their own name to encourage editing
  const businessName = isEditMode && !getBusinessName(provider) ? name : getBusinessName(provider);
  const isInAddressTest = useInTest(CLIENT_PROPROFILE_CLARIFY_LOCATION_TEST_NAME);
  const showAddressTest: boolean = isInAddressTest && !isOwnProfile;
  const assignAddressTest = useAssignTest(
    CLIENT_PROPROFILE_CLARIFY_LOCATION_TEST_NAME,
    CLIENT_PROPROFILE_CLARIFY_LOCATION_TEST_PERCENT,
  );
  const [showAddress, setShowAddress] = useState<boolean>(false);
  const proSchedule = useSelector<RootState, ProviderScheduleDay[]>(
    state => state.providerWorkSchedule.schedules?.[provider.id] || [],
  );

  useEffect(() => {
    if (!isOwnProfile && isProvider) {
      assignAddressTest();
    }
  }, [
    isOwnProfile,
    assignAddressTest,
    isProvider,
  ]);

  const handleMapPress = useCallback(() => {
    const googleMapsLink = googleMapURL(location);
    analytics.track('client_businessinfomodal_map_clicked', {
      provider_id: provider.id,
    }).finally(() => {
      openExternalUrl(googleMapsLink);
    });
  }, [
    provider,
    location,
  ]);

  const handleLocationClick = () => {
    analytics.track('client_proprofile_businesslocation_click', {
      provider_id: provider.id,
    });
    setShowAddress(true);
  };

  const handleBackClick = () => {
    analytics.track('client_businessinfomodal_back_click', {
      provider_id: provider.id,
    });
    setShowAddress(false);
  };

  const handleMessagePro = () => {
    // need to close the modal, otherwise login modal pops in behind it
    setShowAddress(false);
    onMessagePro();
  };

  const isNewPro = daysSince(proCreationTime) <= NEW_PRO_DAYS;
  const headerText = formatNameWithPronouns(name, preferredPronouns);

  // Note: I removed the second half of this conditional (&& isReferralEligible === false)
  // because it's an async assignment and delays the render of this UI too long imo.
  //
  // This means that the useAffect above is basically 100% responsible for the conditional
  // rendering in this component.
  if (showAddressTest) {
    return (
      <View>
        <Stack space={8} alignItems="center">
          {/* Profile Image */}
          <AvatarIcon
            name={provider.name}
            sizeClass="medium"
            icon={profilePhoto ? `${profilePhoto}_200x200.jpg` : null}
            onClick={onProfilePhotoClick}
            testID="profilePhoto"
          />
          {/* Pro name */}
          <HeaderText ariaLevel={1} level={3} testID="proName">{headerText}</HeaderText>
          {/* Rating */}
          <RatingStars
            ratingStars={ratingStars}
            size={15}
            ratingsCount={ratingCount}
            reviewCount={reviewCount}
          />
          {/* Business location */}
          <Pressable onPress={handleLocationClick}>
            <Inline space={4} alignItems="center">
              {location?.mobile_business
                ? <BasicHomeIcon height={12} width={13} />
                : <ShoppingBusinessIcon />}
              <BodyText
                appearance="small"
                textDecoration="underline"
                testID="pro-bio-business-link"
              >
                {consolidateAddress(provider)}
              </BodyText>
            </Inline>
          </Pressable>
          {/* Instagram handle */}
          <InstagramLinkContainer
            provider={provider}
          />
        </Stack>
        {showAddress && (
          <BusinessInfoModal
            isMobile={isMobile}
            provider={provider}
            proSchedule={proSchedule}
            showCallPro={showCallPro}
            onBackPress={handleBackClick}
            onMessagePro={handleMessagePro}
            onCallPro={onCallPro}
            onMapPress={handleMapPress}
          />
        )}
        <Absolute
          top={8}
          right={4}
        >
          <ProfileActions
            isEditMode={isEditMode}
            isBookmarked={isBookmarked}
            provider={provider}
            user={user}
            onBookmarkClick={onBookmarkClick}
            onProfileEditClick={onProfileEditClick}
            shouldShowChat={shouldShowChat}
            onChatClick={onMessageClicked}
          />
        </Absolute>
      </View>
    );
  }

  return (
    <Box>
      <Inline space={16}>
        {/* Profile Image */}
        <Box>
          <Pressable onPress={onProfilePhotoClick} testID="profilePhoto">
            <Inline dataSet={{ photoUrl: profilePhoto }}>
              <AvatarIcon
                name={provider.name}
                sizeClass={isMobile ? 'pro-profile-imge-mobile' : 'pro-profile-imge-desktop'}
                icon={profilePhoto ? `${profilePhoto}_200x200.jpg` : null}
                onClick={onProfilePhotoClick}
              />
            </Inline>
          </Pressable>
        </Box>
        <Stack flexGrow={1} flexShrink={1}>
          <Inline
            flexWrap={isEditMode ? undefined : 'wrap'}
            flexGrow={1}
            maxWidth={200}
          >
            {/* Business card */}
            <Stack
              space={4}
              flexGrow={1}
              flexShrink={1}
              paddingRight={4}
            >
              {/* Pro name */}
              <HeaderText ariaLevel={1} level={3} testID="proName">{name}</HeaderText>
              {/* Profession */}
              <BodyText appearance="small" testID="proProfession">{profession}</BodyText>
              {/* Business name */}
              {businessName
                && <BodyText appearance="small" testID="proBusinessName">Business name: {businessName}</BodyText>}
              {/* New pro badge */}
              {/* {isNewPro && !isTopPro && <NewProBadge />} */}
              {/* Instagram handle */}
              <InstagramLinkContainer
                provider={provider}
              />
              <Inline alignItems="center" space={8}>
                {/* Rating */}
                {ratingCount >= NEW_PRO_MIN_REVIEWS && (
                  <RatingStars
                    ratingStars={ratingStars}
                    size={15}
                    ratingsCount={ratingCount}
                    reviewCount={reviewCount}
                  />
                )}
                {/* Top Pro */}
                {isTopPro && (
                  <Pressable onPress={onTopProClick}>
                    <DeprecatedTopProBadge />
                  </Pressable>
                )}
                {/* New Pro */}
                {isNewPro && !isTopPro && <NewProBadge />}
              </Inline>
            </Stack>
            <ProfileActions
              shareConfig={shareConfig}
              isEditMode={isEditMode}
              isBookmarked={isBookmarked}
              provider={provider}
              user={user}
              onBookmarkClick={onBookmarkClick}
              onProfileEditClick={onProfileEditClick}
              shouldShowChat={shouldShowChat}
              onChatClick={onMessageClicked}
            />
          </Inline>
          {/* Pro edit mode insights */}
          {isEditMode ? <InsightsCTAs providerId={providerId} /> : null}
        </Stack>
      </Inline>
    </Box>
  );
};
